// src/pages/Sozlamalar.js
import React, { useEffect, useState } from "react";
import "./Sozlamalar.css";
import api from "../api";

/* ==== Helpers ==== */
const FIELD = "nom";
const textOf = (item) => item?.[FIELD] ?? "";
const payload = (val) => ({ [FIELD]: val });

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

const byNameExists = (list, val) =>
  list.some(
    (i) =>
      String(textOf(i)).trim().toLowerCase() ===
      String(val).trim().toLowerCase()
  );

export default function Sozlamalar() {
  // Serverdan keladigan ro‘yxatlar
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [telegramSklad, setTelegramSklad] = useState("");
  const [telegramOrderChatId, setTelegramOrderChatId] = useState("");
  const [dailyReportChatId, setDailyReportChatId] = useState("");
  const [elonAdminIds, setElonAdminIds] = useState("");
  const [dollarKurs, setDollarKurs] = useState("");

  // Yangi elementlar
  const [newCategory, setNewCategory] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [newPriceType, setNewPriceType] = useState("");

  // Sklad forma (ko'p maydonli)
  const EMPTY_SKLAD = { nom: "", telefon: "", location: "", maps_link: "", rasm: null, seriya_prefix: "YT-" };
  const [skladForm, setSkladForm] = useState(EMPTY_SKLAD);
  const [skladPreview, setSkladPreview] = useState(null);

  // Sklad tahrirlash modali
  const [skladModal, setSkladModal] = useState(null); // { item } yoki null
  const [skladModalPreview, setSkladModalPreview] = useState(null);

  // Inline tahrirlash holati
  const [editState, setEditState] = useState(null); // { id, val, endpoint, setListFn, keyPrefix }

  // UI holatlari
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(""); // "cat|brand|price|wh" + action
  const [err, setErr] = useState(null);

  // Dastlabki yuklash
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [c, b, p, w, s, k] = await Promise.all([
          api.get("maxsulot-kategoriyasi/"),
          api.get("brend/"),
          api.get("narx-turi/"),
          api.get("sklad/"),
          api.get("system-settings/"),
          api.get("kurs/").catch(() => ({ data: {} })),
        ]);
        setCategories(asList(c.data));
        setBrands(asList(b.data));
        setPriceTypes(asList(p.data));
        setWarehouses(asList(w.data));
        setTelegramSklad(s.data?.telegram_sklad ? String(s.data.telegram_sklad) : "");
        setTelegramOrderChatId(s.data?.telegram_order_chat_id || "");
        setDailyReportChatId(s.data?.daily_report_chat_id || "");
        setElonAdminIds(s.data?.elon_admin_ids || "");
        setDollarKurs(k.data?.naqd != null ? String(k.data.naqd) : "");
      } catch (e) {
        console.log("LOAD ERR:", e?.response?.status, e?.response?.data);
        setErr("Ma'lumotlarni yuklashda xatolik yuz berdi.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ------- Qo'shish (POST) ------- */
  const addCategory = async () => {
    const v = newCategory.trim();
    if (!v) return;
    if (byNameExists(categories, v)) return alert("Bu kategoriya allaqachon mavjud.");
    setBusyKey("cat:add");
    try {
      const { data } = await api.post("maxsulot-kategoriyasi/", payload(v));
      setCategories((prev) => [...prev, data]);
      setNewCategory("");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Kategoriya qo'shishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const addBrand = async () => {
    const v = newBrand.trim();
    if (!v) return;
    if (byNameExists(brands, v)) return alert("Bu brand allaqachon mavjud.");
    setBusyKey("brand:add");
    try {
      const { data } = await api.post("brend/", payload(v));
      setBrands((prev) => [...prev, data]);
      setNewBrand("");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Brand qo'shishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const addPriceType = async () => {
    const v = newPriceType.trim();
    if (!v) return;
    if (byNameExists(priceTypes, v)) return alert("Bu narx turi allaqachon mavjud.");
    setBusyKey("price:add");
    try {
      const { data } = await api.post("narx-turi/", payload(v));
      setPriceTypes((prev) => [...prev, data]);
      setNewPriceType("");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Narx turi qo'shishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const addWarehouse = async () => {
    const nom = skladForm.nom.trim();
    if (!nom) return alert("Sklad nomi bo'sh bo'lishi mumkin emas.");
    if (byNameExists(warehouses, nom)) return alert("Bu sklad allaqachon mavjud.");
    setBusyKey("wh:add");
    try {
      const fd = new FormData();
      fd.append("nom", nom);
      if (skladForm.telefon) fd.append("telefon", skladForm.telefon);
      if (skladForm.location) fd.append("location", skladForm.location);
      if (skladForm.maps_link) fd.append("maps_link", skladForm.maps_link);
      fd.append("seriya_prefix", skladForm.seriya_prefix || "YT-");
      if (skladForm.rasm) fd.append("rasm", skladForm.rasm);
      const { data } = await api.post("sklad/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWarehouses((prev) => [...prev, data]);
      setSkladForm(EMPTY_SKLAD);
      setSkladPreview(null);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || JSON.stringify(e?.response?.data) || "Sklad qo'shishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const openSkladModal = (item) => {
    setSkladModal({ item: { ...item } });
    setSkladModalPreview(item.rasm || null);
  };

  const closeSkladModal = () => {
    setSkladModal(null);
    setSkladModalPreview(null);
  };

  const saveSkladModal = async () => {
    const item = skladModal?.item;
    if (!item) return;
    if (!item.nom.trim()) return alert("Sklad nomi bo'sh bo'lishi mumkin emas.");
    setBusyKey(`wh:edit:${item.id}`);
    try {
      const fd = new FormData();
      fd.append("nom", item.nom.trim());
      fd.append("telefon", item.telefon || "");
      fd.append("location", item.location || "");
      fd.append("maps_link", item.maps_link || "");
      fd.append("seriya_prefix", item.seriya_prefix || "YT-");
      if (item._newRasm) fd.append("rasm", item._newRasm);
      const { data } = await api.patch(`sklad/${item.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWarehouses((prev) => prev.map((x) => (x.id === item.id ? data : x)));
      closeSkladModal();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Tahrirlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  /* ------- O'chirish (DELETE) ------- */
  const deleteCategory = async (id) => {
    if (!window.confirm("Kategoriya o‘chirilsinmi?")) return;
    setBusyKey(`cat:del:${id}`);
    try {
      await api.delete(`maxsulot-kategoriyasi/${id}/`);
      setCategories((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Kategoriya o'chirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const deleteBrand = async (id) => {
    if (!window.confirm("Brand o‘chirilsinmi?")) return;
    setBusyKey(`brand:del:${id}`);
    try {
      await api.delete(`brend/${id}/`);
      setBrands((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Brand o'chirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const deletePriceType = async (id) => {
    if (!window.confirm("Narx turi o‘chirilsinmi?")) return;
    setBusyKey(`price:del:${id}`);
    try {
      await api.delete(`narx-turi/${id}/`);
      setPriceTypes((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Narx turi o'chirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const deleteWarehouse = async (id) => {
    if (!window.confirm("Sklad o‘chirilsinmi?")) return;
    setBusyKey(`wh:del:${id}`);
    try {
      await api.delete(`sklad/${id}/`);
      setWarehouses((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Sklad o'chirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const changeSkladCurrency = async (item, value) => {
    setBusyKey(`wh:cur:${item.id}`);
    try {
      const { data } = await api.patch(`sklad/${item.id}/`, { currency_mode: value });
      setWarehouses((prev) =>
        prev.map((x) => (x.id === item.id ? { ...x, currency_mode: data?.currency_mode ?? value } : x))
      );
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Valyutani o'zgartirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const changeSkladShowInShop = async (item, value) => {
    setBusyKey(`wh:shop:${item.id}`);
    try {
      const { data } = await api.patch(`sklad/${item.id}/`, { show_in_shop: value });
      setWarehouses((prev) =>
        prev.map((x) => (x.id === item.id ? { ...x, show_in_shop: data?.show_in_shop ?? value } : x))
      );
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "O'zgartirishda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const clearWarehouseProducts = async (item) => {
    const ok = window.confirm(
      `"${textOf(item)}" skladdagi faol mahsulotlar tozalansinmi?\n\n` +
        "Tarixga bog'langan mahsulotlar arxivga olinadi, qolganlari o'chiriladi."
    );
    if (!ok) return;

    setBusyKey(`wh:clear:${item.id}`);
    try {
      const { data } = await api.post(`sklad/${item.id}/clear-products/`);
      alert(data?.detail || "Sklad mahsulotlari tozalandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Sklad mahsulotlarini tozalashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const saveTelegramSklad = async () => {
    setBusyKey("tg:save");
    try {
      const payload = {
        telegram_sklad: telegramSklad ? Number(telegramSklad) : null,
      };
      const { data } = await api.patch("system-settings/", payload);
      setTelegramSklad(data?.telegram_sklad ? String(data.telegram_sklad) : "");
      alert("Telegram bot sklad sozlamasi saqlandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Telegram sozlamasini saqlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const saveTelegramOrderChatId = async () => {
    setBusyKey("tg:order:save");
    try {
      const { data } = await api.patch("system-settings/", {
        telegram_order_chat_id: telegramOrderChatId.trim(),
      });
      setTelegramOrderChatId(data?.telegram_order_chat_id || "");
      alert("Buyurtmalar uchun Telegram guruh saqlandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Saqlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const saveDollarKurs = async () => {
    const val = Number(String(dollarKurs).replace(/\s/g, ""));
    if (!val || val <= 0) {
      alert("Kurs 0 dan katta son bo'lishi kerak.");
      return;
    }
    setBusyKey("kurs:save");
    try {
      const { data } = await api.patch("kurs/", { naqd: val, karta: val });
      setDollarKurs(data?.naqd != null ? String(data.naqd) : String(val));
      alert("Dollar kursi saqlandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Kursni saqlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const saveDailyReportChatId = async () => {
    setBusyKey("tg:report:save");
    try {
      const { data } = await api.patch("system-settings/", {
        daily_report_chat_id: dailyReportChatId.trim(),
      });
      setDailyReportChatId(data?.daily_report_chat_id || "");
      alert("Kunlik hisobot chat ID saqlandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Saqlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  const saveElonAdminIds = async () => {
    setBusyKey("tg:elon:save");
    try {
      const { data } = await api.patch("system-settings/", {
        elon_admin_ids: elonAdminIds,
      });
      setElonAdminIds(data?.elon_admin_ids || "");
      alert("E'lon yuboruvchi adminlar saqlandi.");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Saqlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  /* ------- Inline tahrirlash ------- */
  const startEdit = (item, endpoint, setListFn, keyPrefix) => {
    setEditState({ id: item.id, val: textOf(item), endpoint, setListFn, keyPrefix });
  };

  const cancelEdit = () => setEditState(null);

  const saveEdit = async () => {
    if (!editState) return;
    const { id, val, endpoint, setListFn, keyPrefix } = editState;
    const next = val.trim();
    if (!next) return;
    setBusyKey(`${keyPrefix}:edit:${id}`);
    try {
      const { data } = await api.patch(`${endpoint}/${id}/`, payload(next));
      setListFn((prev) => prev.map((x) => (x.id === id ? data : x)));
      setEditState(null);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Tahrirlashda xato.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="sozlamalar-page yu-tech-container">
      <header className="yu-tech-header fr-panel">
        <h1>YU tech</h1>
      </header>

      <div className="yu-tech-content">
        {/* Kategoriya */}
        <div className="yu-tech-section fr-card">
          <h2>Kategoriya qo'shish</h2>
          <div className="input-group">
            <input
              type="text"
              className="yu-tech-input"
              placeholder="Yangi kategoriya..."
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
            />
            <button
              className="yu-tech-add-btn"
              onClick={addCategory}
              disabled={!newCategory.trim() || busyKey === "cat:add"}
              title="Qo‘shish"
            >
              {busyKey === "cat:add" ? "…" : "+"}
            </button>
          </div>
          <ul className="yu-tech-list">
            {categories.map((item) => {
              const delBusy = busyKey === `cat:del:${item.id}`;
              const editBusy = busyKey === `cat:edit:${item.id}`;
              const isEditing = editState?.id === item.id && editState?.keyPrefix === "cat";
              return (
                <li key={item.id} className="yu-tech-list-item">
                  {isEditing ? (
                    <>
                      <input
                        className="inline-edit-input"
                        value={editState.val}
                        autoFocus
                        onChange={(e) => setEditState((p) => ({ ...p, val: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                      />
                      <div className="item-actions">
                        <button className="edit-btn" onClick={saveEdit} disabled={editBusy}>
                          {editBusy ? "…" : "Saqlash"}
                        </button>
                        <button className="delete-btn" onClick={cancelEdit}>Bekor</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>- {textOf(item)}</span>
                      <div className="item-actions">
                        <button
                          className="edit-btn"
                          onClick={() => startEdit(item, "maxsulot-kategoriyasi", setCategories, "cat")}
                          disabled={editBusy || delBusy}
                        >
                          Tahrirlash
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => deleteCategory(item.id)}
                          disabled={delBusy || editBusy}
                        >
                          {delBusy ? "…" : "O'chirish"}
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Brand */}
        <div className="yu-tech-section fr-card">
          <h2>Brand qo'shish</h2>
          <div className="input-group">
            <input
              type="text"
              className="yu-tech-input"
              placeholder="Yangi brand..."
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addBrand()}
            />
            <button
              className="yu-tech-add-btn"
              onClick={addBrand}
              disabled={!newBrand.trim() || busyKey === "brand:add"}
            >
              {busyKey === "brand:add" ? "…" : "+"}
            </button>
          </div>
          <ul className="yu-tech-list">
            {brands.map((item) => {
              const delBusy = busyKey === `brand:del:${item.id}`;
              const editBusy = busyKey === `brand:edit:${item.id}`;
              const isEditing = editState?.id === item.id && editState?.keyPrefix === "brand";
              return (
                <li key={item.id} className="yu-tech-list-item">
                  {isEditing ? (
                    <>
                      <input
                        className="inline-edit-input"
                        value={editState.val}
                        autoFocus
                        onChange={(e) => setEditState((p) => ({ ...p, val: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                      />
                      <div className="item-actions">
                        <button className="edit-btn" onClick={saveEdit} disabled={editBusy}>
                          {editBusy ? "…" : "Saqlash"}
                        </button>
                        <button className="delete-btn" onClick={cancelEdit}>Bekor</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>- {textOf(item)}</span>
                      <div className="item-actions">
                        <button
                          className="edit-btn"
                          onClick={() => startEdit(item, "brend", setBrands, "brand")}
                          disabled={editBusy || delBusy}
                        >
                          Tahrirlash
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => deleteBrand(item.id)}
                          disabled={delBusy || editBusy}
                        >
                          {delBusy ? "…" : "O'chirish"}
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Narx turlari */}
        <div className="yu-tech-section fr-card">
          <h2>Narx turlarini qo'shish</h2>
          <div className="input-group">
            <input
              type="text"
              className="yu-tech-input"
              placeholder="Yangi narx turi..."
              value={newPriceType}
              onChange={(e) => setNewPriceType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addPriceType()}
            />
            <button
              className="yu-tech-add-btn"
              onClick={addPriceType}
              disabled={!newPriceType.trim() || busyKey === "price:add"}
            >
              {busyKey === "price:add" ? "…" : "+"}
            </button>
          </div>
          <ul className="yu-tech-list">
            {priceTypes.map((item) => {
              const delBusy = busyKey === `price:del:${item.id}`;
              const editBusy = busyKey === `price:edit:${item.id}`;
              const isEditing = editState?.id === item.id && editState?.keyPrefix === "price";
              return (
                <li key={item.id} className="yu-tech-list-item">
                  {isEditing ? (
                    <>
                      <input
                        className="inline-edit-input"
                        value={editState.val}
                        autoFocus
                        onChange={(e) => setEditState((p) => ({ ...p, val: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                      />
                      <div className="item-actions">
                        <button className="edit-btn" onClick={saveEdit} disabled={editBusy}>
                          {editBusy ? "…" : "Saqlash"}
                        </button>
                        <button className="delete-btn" onClick={cancelEdit}>Bekor</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>- {textOf(item)}</span>
                      <div className="item-actions">
                        <button
                          className="edit-btn"
                          onClick={() => startEdit(item, "narx-turi", setPriceTypes, "price")}
                          disabled={editBusy || delBusy}
                        >
                          Tahrirlash
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => deletePriceType(item.id)}
                          disabled={delBusy || editBusy}
                        >
                          {delBusy ? "…" : "O'chirish"}
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Sklad */}
        <div className="yu-tech-section fr-card sklad-section">
          <h2>Do'kon / Sklad qo'shish</h2>

          {/* Yangi sklad formasi */}
          <div className="sklad-form">
            {/* Rasm upload */}
            <div className="sklad-img-upload">
              <label className="sklad-img-label" htmlFor="sklad-rasm-input">
                {skladPreview
                  ? <img src={skladPreview} alt="preview" className="sklad-img-circle" />
                  : <span className="sklad-img-placeholder">📷</span>}
              </label>
              <input
                id="sklad-rasm-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setSkladForm((p) => ({ ...p, rasm: f }));
                  setSkladPreview(URL.createObjectURL(f));
                }}
              />
              <span className="sklad-img-hint">Logo</span>
            </div>

            {/* Maydonlar */}
            <div className="sklad-fields">
              <input
                type="text"
                className="yu-tech-input sklad-input"
                placeholder="Do'kon / sklad nomi *"
                value={skladForm.nom}
                onChange={(e) => setSkladForm((p) => ({ ...p, nom: e.target.value }))}
              />
              <input
                type="tel"
                className="yu-tech-input sklad-input"
                placeholder="Javobgar shaxs tel. raqami"
                value={skladForm.telefon}
                onChange={(e) => setSkladForm((p) => ({ ...p, telefon: e.target.value }))}
              />
              <input
                type="text"
                className="yu-tech-input sklad-input"
                placeholder="Manzil (shahar, ko'cha...)"
                value={skladForm.location}
                onChange={(e) => setSkladForm((p) => ({ ...p, location: e.target.value }))}
              />
              <div className="sklad-maps-row">
                <input
                  type="url"
                  className="yu-tech-input sklad-input"
                  placeholder="Google Maps link"
                  value={skladForm.maps_link}
                  onChange={(e) => setSkladForm((p) => ({ ...p, maps_link: e.target.value }))}
                />
                {skladForm.maps_link && (
                  <a
                    href={skladForm.maps_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="maps-btn"
                    title="Xaritada ko'rish"
                  >
                    📍
                  </a>
                )}
              </div>
              <input
                type="text"
                className="yu-tech-input sklad-input"
                placeholder="Seriya prefiksi (masalan: YT-, KT-)"
                value={skladForm.seriya_prefix}
                onChange={(e) => setSkladForm((p) => ({ ...p, seriya_prefix: e.target.value }))}
              />
              <button
                className="yu-tech-add-btn sklad-add-btn"
                onClick={addWarehouse}
                disabled={!skladForm.nom.trim() || busyKey === "wh:add"}
              >
                {busyKey === "wh:add" ? "Saqlanmoqda…" : "Sklad qo'shish +"}
              </button>
            </div>
          </div>

          {/* Sklad ro'yxati */}
          <ul className="yu-tech-list sklad-list">
            {warehouses.map((item) => {
              const delBusy = busyKey === `wh:del:${item.id}`;
              const editBusy = busyKey === `wh:edit:${item.id}`;
              const clearBusy = busyKey === `wh:clear:${item.id}`;
              return (
                <li key={item.id} className="sklad-list-item">
                  {/* Avatar */}
                  <div className="sklad-item-avatar">
                    {item.rasm
                      ? <img src={item.rasm} alt={item.nom} className="sklad-img-circle sm" />
                      : <span className="sklad-avatar-letter">{item.nom?.[0]?.toUpperCase() || "?"}</span>}
                  </div>
                  {/* Ma'lumotlar */}
                  <div className="sklad-item-info">
                    <strong>{item.nom}</strong>
                    {item.telefon && <span className="sklad-meta">📞 {item.telefon}</span>}
                    {item.location && <span className="sklad-meta">📍 {item.location}</span>}
                    {item.maps_link && (
                      <a href={item.maps_link} target="_blank" rel="noopener noreferrer" className="sklad-maps-link">
                        Xaritada ko'rish
                      </a>
                    )}
                    <label className="sklad-meta" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      Savdo valyutasi:
                      <select
                        value={item.currency_mode || "som"}
                        onChange={(e) => changeSkladCurrency(item, e.target.value)}
                        disabled={busyKey === `wh:cur:${item.id}`}
                        style={{ minHeight: 30, padding: "2px 8px" }}
                      >
                        <option value="som">so'm</option>
                        <option value="usd">$ dollar</option>
                      </select>
                    </label>
                    <label className="sklad-meta" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={item.show_in_shop !== false}
                        onChange={(e) => changeSkladShowInShop(item, e.target.checked)}
                        disabled={busyKey === `wh:shop:${item.id}`}
                      />
                      Bozorda ko'rinsin
                    </label>
                  </div>
                  {/* Tugmalar */}
                  <div className="item-actions">
                    <button
                      className="edit-btn"
                      onClick={() => openSkladModal(item)}
                      disabled={editBusy || delBusy || clearBusy}
                    >
                      {editBusy ? "…" : "Tahrirlash"}
                    </button>
                    <button
                      className="clear-btn"
                      onClick={() => clearWarehouseProducts(item)}
                      disabled={clearBusy || editBusy || delBusy}
                    >
                      {clearBusy ? "..." : "Tozalash"}
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => deleteWarehouse(item.id)}
                      disabled={delBusy || editBusy || clearBusy}
                    >
                      {delBusy ? "…" : "O'chirish"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Sklad tahrirlash modali */}
        {skladModal && (
          <div className="modal-backdrop" onClick={closeSkladModal}>
            <div className="sklad-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sklad-modal-head">
                <h3>Skladni tahrirlash</h3>
                <button className="yu-tech-add-btn" onClick={closeSkladModal} style={{ padding: "4px 12px" }}>✕</button>
              </div>
              <div className="sklad-modal-body">
                {/* Rasm */}
                <div className="sklad-img-upload">
                  <label className="sklad-img-label" htmlFor="sklad-modal-rasm">
                    {skladModalPreview
                      ? <img src={skladModalPreview} alt="preview" className="sklad-img-circle" />
                      : <span className="sklad-img-placeholder">📷</span>}
                  </label>
                  <input
                    id="sklad-modal-rasm"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setSkladModal((p) => ({ ...p, item: { ...p.item, _newRasm: f } }));
                      setSkladModalPreview(URL.createObjectURL(f));
                    }}
                  />
                  <span className="sklad-img-hint">Logo o'zgartirish</span>
                </div>
                <input
                  type="text"
                  className="yu-tech-input sklad-input"
                  placeholder="Sklad nomi *"
                  value={skladModal.item.nom}
                  onChange={(e) => setSkladModal((p) => ({ ...p, item: { ...p.item, nom: e.target.value } }))}
                />
                <input
                  type="tel"
                  className="yu-tech-input sklad-input"
                  placeholder="Telefon raqami"
                  value={skladModal.item.telefon || ""}
                  onChange={(e) => setSkladModal((p) => ({ ...p, item: { ...p.item, telefon: e.target.value } }))}
                />
                <input
                  type="text"
                  className="yu-tech-input sklad-input"
                  placeholder="Manzil"
                  value={skladModal.item.location || ""}
                  onChange={(e) => setSkladModal((p) => ({ ...p, item: { ...p.item, location: e.target.value } }))}
                />
                <div className="sklad-maps-row">
                  <input
                    type="url"
                    className="yu-tech-input sklad-input"
                    placeholder="Google Maps link"
                    value={skladModal.item.maps_link || ""}
                    onChange={(e) => setSkladModal((p) => ({ ...p, item: { ...p.item, maps_link: e.target.value } }))}
                  />
                  {skladModal.item.maps_link && (
                    <a
                      href={skladModal.item.maps_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="maps-btn"
                    >
                      📍
                    </a>
                  )}
                </div>
                <input
                  type="text"
                  className="yu-tech-input sklad-input"
                  placeholder="Seriya prefiksi (masalan: YT-, KT-)"
                  value={skladModal.item.seriya_prefix || ""}
                  onChange={(e) => setSkladModal((p) => ({ ...p, item: { ...p.item, seriya_prefix: e.target.value } }))}
                />
              </div>
              <div className="sklad-modal-foot">
                <button
                  className="yu-tech-add-btn"
                  onClick={saveSkladModal}
                  disabled={busyKey.startsWith("wh:edit")}
                >
                  {busyKey.startsWith("wh:edit") ? "Saqlanmoqda…" : "Saqlash"}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && <div className="muted">Yuklanmoqda…</div>}
        <div className="yu-tech-section fr-card">
          <h2>Dollar kursi</h2>
          <div className="telegram-settings">
            <label className="telegram-label" htmlFor="dollar-kurs-input">
              1 $ = ? so'm
            </label>
            <input
              id="dollar-kurs-input"
              type="number"
              min="1"
              className="yu-tech-input"
              placeholder="Masalan: 12500"
              value={dollarKurs}
              onChange={(e) => setDollarKurs(e.target.value)}
            />
            <div className="muted">
              Mahsulotlar dollarda kiritiladi. Sotuv, kassa va qarzlar shu kurs bo'yicha so'mga aylantiriladi.
              Kurs o'zgartirilsa, sotilmagan mahsulotlarning so'm narxi yangilanadi (sotilgan qarzlar o'zgarmaydi).
            </div>
            <button
              className="edit-btn"
              onClick={saveDollarKurs}
              disabled={busyKey === "kurs:save"}
              style={{ marginTop: "8px" }}
            >
              {busyKey === "kurs:save" ? "..." : "Saqlash"}
            </button>
          </div>
        </div>

        <div className="yu-tech-section fr-card">
          <h2>Telegram bot</h2>
          <div className="telegram-settings">
            <label className="telegram-label" htmlFor="telegram-sklad-select">
              Asosiy sklad
            </label>
            <select
              id="telegram-sklad-select"
              className="telegram-select"
              value={telegramSklad}
              onChange={(e) => setTelegramSklad(e.target.value)}
            >
              <option value="">Tanlanmagan</option>
              {warehouses.map((item) => (
                <option key={item.id} value={item.id}>
                  {textOf(item)}
                </option>
              ))}
            </select>
            <div className="muted">
              Bot qidiruvi shu skladdagi mahsulot asosida ishlaydi. Boshqa skladlar bo‘yicha faqat qoldiq soni ko‘rsatiladi.
            </div>
            <button
              className="edit-btn"
              onClick={saveTelegramSklad}
              disabled={busyKey === "tg:save"}
            >
              {busyKey === "tg:save" ? "..." : "Saqlash"}
            </button>

            <div style={{ marginTop: "20px", borderTop: "1px solid var(--border, #e5e7eb)", paddingTop: "16px" }}>
              <label className="telegram-label" htmlFor="telegram-order-chat-id">
                Buyurtmalar guruhi (Chat ID)
              </label>
              <input
                id="telegram-order-chat-id"
                type="text"
                className="yu-tech-input"
                placeholder="-1001234567890"
                value={telegramOrderChatId}
                onChange={(e) => setTelegramOrderChatId(e.target.value)}
              />
              <div className="muted">
                E-commerce buyurtmalar shu Telegram guruhiga yuboriladi. Guruh Chat ID sini kiriting (masalan: -1001234567890).
              </div>
              <button
                className="edit-btn"
                onClick={saveTelegramOrderChatId}
                disabled={busyKey === "tg:order:save"}
                style={{ marginTop: "8px" }}
              >
                {busyKey === "tg:order:save" ? "..." : "Saqlash"}
              </button>
            </div>

            <div style={{ marginTop: "20px", borderTop: "1px solid var(--border, #e5e7eb)", paddingTop: "16px" }}>
              <label className="telegram-label" htmlFor="daily-report-chat-id">
                Kunlik hisobot guruhi (Chat ID)
              </label>
              <input
                id="daily-report-chat-id"
                type="text"
                className="yu-tech-input"
                placeholder="-1001234567890"
                value={dailyReportChatId}
                onChange={(e) => setDailyReportChatId(e.target.value)}
              />
              <div className="muted">
                Har kuni soat <b>22:00</b> da shu chatga savdo va foyda hisoboti yuboriladi:
                qaysi do'konда qaysi admin qancha savdo va foyda qilgani (barcha do'konlar).
              </div>
              <button
                className="edit-btn"
                onClick={saveDailyReportChatId}
                disabled={busyKey === "tg:report:save"}
                style={{ marginTop: "8px" }}
              >
                {busyKey === "tg:report:save" ? "..." : "Saqlash"}
              </button>
            </div>

            <div style={{ marginTop: "20px", borderTop: "1px solid var(--border, #e5e7eb)", paddingTop: "16px" }}>
              <label className="telegram-label" htmlFor="elon-admin-ids">
                E'lon yuboruvchi adminlar (Chat ID)
              </label>
              <textarea
                id="elon-admin-ids"
                className="yu-tech-input"
                style={{ width: "100%", minHeight: "80px", resize: "vertical" }}
                placeholder="Masalan: 123456789, 987654321"
                value={elonAdminIds}
                onChange={(e) => setElonAdminIds(e.target.value)}
              />
              <div className="muted">
                Bu yerdagi chat_id egalari botga <b>/elon</b> orqali barcha haridorlarga e'lon yubora oladi.
                chat_id ni olish uchun botga <b>/id</b> deb yozing. Bir nechta bo'lsa vergul bilan ajrating.
              </div>
              <button
                className="edit-btn"
                onClick={saveElonAdminIds}
                disabled={busyKey === "tg:elon:save"}
                style={{ marginTop: "8px" }}
              >
                {busyKey === "tg:elon:save" ? "..." : "Saqlash"}
              </button>
            </div>
          </div>
        </div>

        {err && <div className="error">{err}</div>}
      </div>
    </div>
  );
}
